/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import { useFormik, type FormikHelpers } from 'formik'
import isEqual from 'lodash/isEqual'
import { ReactNode, useMemo, type FC } from 'react'
import { Button, Col, FloatingLabel, Form, Row } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

const validationSchema = yup.object().shape({
  sku: yup.string().nullable(),
  name: yup.string().nullable(),
  synonym: yup.string().nullable(),
  textlink: yup.string().nullable(),
  pageTitle: yup.string().nullable(),
  description: yup.string().nullable(),
  metaTagDescription: yup.string().nullable(),
  group: yup.object().shape({
    section: yup.object().shape({
      id: yup.string().nullable(),
      name: yup.string().nullable(),
      placeholder: yup.string().nullable(),
    }).nullable(),
    category: yup.object().shape({
      id: yup.string().nullable(),
      name: yup.string().nullable(),
      placeholder: yup.string().nullable(),
    }).nullable(),
  }),
  brand: yup.object().shape({
    id: yup.string().nullable(),
    name: yup.string().nullable(),
    placeholder: yup.string().nullable(),
  }).nullable(),
  createdAt: yup.string().nullable(),
  reference: yup.string().nullable(),
  ean: yup.string().nullable(),
  wikimeeMeta: yup.mixed().nullable(),
  wikimeeMetaName: yup.string().nullable(),
  wikimeeMetaVisibleOnSite: yup.boolean().nullable(),
  wikimeeMetaBrand: yup.string().nullable(),
  wikimeeMetaType: yup.string().nullable(),
  wikimeeMetaUnit: yup.string().nullable(),
  wikimeeMetaWeight: yup.string().nullable(),
  wikimeeMetaDetail: yup.string().nullable(),
  image: yup.string().nullable(),
  imageLink: yup.string().nullable(),
  updatedAt: yup.string().nullable(),
  status: yup.string().nullable(),
})

export type ValidationSchema = yup.InferType<typeof validationSchema>

export type FormProductHandleSubmit<T = void> = (
  values: ValidationSchema,
  helpers: FormikHelpers<ValidationSchema>
) => T | Promise<T>

export type Props = {
  onSubmit?: FormProductHandleSubmit,
  initialValues?: Partial<ValidationSchema>,
  isLoading?: boolean,
  readOnly?: boolean,
  customFooterMessage?: ReactNode
}

/**
 * FormProduct is described here.
 *
 * ## Usage
 * ```js
 * import * as React from 'react'
 * import FormProduct from './src/views/PostEdit/FormProduct.tsx'
 *
 * const MyComponent = () => (
 *   <FormProduct />
 * )
 *
 * export default MyComponent
 * ```
 */
const FormProduct: FC<Props> = ({
  onSubmit = () => { },
  initialValues = {},
  isLoading = false,
  readOnly = false,
  customFooterMessage = null
}) => {
  const navigate = useNavigate()
  const formik = useFormik({
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: false,
    enableReinitialize: true,
    validationSchema,
    initialValues: validationSchema.cast(initialValues),
    onSubmit
  })

  const isChanged: boolean = useMemo(() => {
    return !isEqual(formik.values, formik.initialValues)
  }, [formik.values, formik.initialValues])

  return (
    <Form className='mb-3 mt-3' onSubmit={formik.handleSubmit}>
      <Row>
        <Col md={12}>
          <h5>Informações gerais</h5>
          <p className='m-b-20'>
            Veja e edite as informações gerais do produto.
          </p>
        </Col>
        <Form.Group as={Col} md={6} className="mb-3">
          <FloatingLabel label="Código SKU">
            <Form.Control
              type='text'
              placeholder='Informe o código SKU'
              name='sku'
              value={formik?.values?.sku ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled
            />
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={6} className="mb-3">
          <FloatingLabel label="EAN">
            <Form.Control
              type='text'
              name='ean'
              value={formik?.values?.ean ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled
            />
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <FloatingLabel label="Nome">
            <Form.Control
              type='text'
              name='name'
              value={formik?.values?.name ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled
            />
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <FloatingLabel label="Descrição (utilizado como nome do produto no site vindo da WikiMee)">
            <Form.Control
              type='text'
              name='description'
              value={formik?.values?.description ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled
            />
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={12} className='mb-3'>
          <FloatingLabel label="Visibilidade">
            <Form.Select
              name='status'
              value={formik?.values?.status ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
            >
              <option>Selecione</option>
              <option value='active'>🌎 Pública</option>
              <option value='inactive'>🔒 Privada</option>
            </Form.Select>
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={6} className="mb-3">
          <FloatingLabel label="Categoria">
            <Form.Control
              type='text'
              name='section'
              value={formik?.values?.group?.section?.placeholder ?? formik?.values?.group?.section?.name ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled
            />
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={6} className="mb-3">
          <FloatingLabel label="Subcategoria">
            <Form.Control
              type='text'
              name='category'
              value={formik?.values?.group?.category?.placeholder ?? formik?.values?.group?.category?.name ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled
            />
          </FloatingLabel>
        </Form.Group>
        <Col md={12}>
          <h5>Wikimee</h5>
          <p className='m-b-20'>
            Abaixo os detalhes do produto recuperados via integração com o Wikimee.
          </p>
        </Col>
        {Object.keys(formik.values.wikimeeMeta ?? {}).filter(tagId => /\d+/g.test(tagId)).map(tagId => {
          const { Campo, Valor } = (formik?.values?.wikimeeMeta as any)?.[tagId] as any

          return (
            <Form.Group as={Col} md={4} className="mb-3">
              <FloatingLabel label={`${Campo} (tag ${tagId})`}>
                <Form.Control
                  type='text'
                  name={tagId}
                  value={(Valor === '' || !Valor) ? 'Não informado' : Valor}
                  disabled
                />
              </FloatingLabel>
            </Form.Group>
          )
        })}
        <Form.Group as={Col} md={4} className="mb-3">
          <FloatingLabel label='Data da criação da tarefa'>
            <Form.Control
              type='text'
              name='modificacao'
              value={(formik?.values?.wikimeeMeta as any)?.inclusao}
              disabled
            />
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={4} className="mb-3">
          <FloatingLabel label='Data da última alteração da tarefa'>
            <Form.Control
              type='text'
              name='modificacao'
              value={(formik?.values?.wikimeeMeta as any)?.modificacao === '0000-00-00 00:00:00'
                ? (formik?.values?.wikimeeMeta as any)?.inclusao
                : (formik?.values?.wikimeeMeta as any)?.modificacao}
              disabled
            />
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Ativo na Wikimee para ser visualizado no site?</Form.Label>
          <Form.Check // prettier-ignore
            type="switch"
            label="Definido como ativo na Tag 185 da Wikimee para que possa ser visualizado no site?"
            name='wikimeeMetaVisibleOnSite'
            checked={formik?.values?.wikimeeMetaVisibleOnSite === true}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled
          />
        </Form.Group>
        <Col md={12}>
          <Button
            variant='outline-primary'
            style={{ marginRight: 10 }}
            onClick={() => navigate('/products')}
            disabled={formik.isSubmitting || isLoading}
          >
            Voltar
          </Button>
          <Button type="submit" disabled={!formik.isValid || formik.isSubmitting || !isChanged || isLoading}>
            Salvar
          </Button>
          {' '}
          {customFooterMessage}
        </Col>
      </Row>
    </Form>
  )
}

export default FormProduct
